<script setup lang="ts">
import { sleep } from "@/vf"
import { onMounted, provide, reactive, ref, type ComponentPublicInstance } from "vue"

const props = withDefaults(
    defineProps<{
        openFirst?: boolean
        open?: string | null
        layout?: "frontend" | "backend" | "list"
        lazy?: boolean
        preload?: boolean
        preloadDelay?: number
    }>(),
    {
        openFirst: false,
        open: null,
        layout: "frontend",
        lazy: false,
        preload: false,
        preloadDelay: 1000,
    },
)

const activeTab = ref<string | number | null>(props.open)
const tabs = reactive<Record<string | number, HTMLElement | ComponentPublicInstance | null>>({})
const preloadedItems = ref<Array<string | number>>([])
const registeredItemIds: Array<string | number> = []

async function selectTab(itemId: string | number) {
    activeTab.value = activeTab.value === itemId ? null : itemId

    if (activeTab.value !== null) {
        await sleep(500)
        ;(tabs[itemId] as HTMLElement)?.scrollIntoView({ behavior: "smooth" })
    }
}

let isFirstItemRegistered = false

function registerTab(id: string | number, element: HTMLElement | ComponentPublicInstance | null) {
    tabs[id] = element
    registeredItemIds.push(id)

    // If openFirst is true and this is the first item registered, open it
    if (props.openFirst && !isFirstItemRegistered) {
        activeTab.value = id
        isFirstItemRegistered = true
    }
}

// Preload items when the browser is idling
onMounted(() => {
    if (props.lazy && props.preload) {
        const schedulePreload = (callback: () => void) => {
            if ("requestIdleCallback" in window) {
                requestIdleCallback(callback)
            } else {
                setTimeout(callback, props.preloadDelay)
            }
        }

        schedulePreload(() => {
            let delay = 0
            registeredItemIds.forEach(id => {
                setTimeout(() => {
                    preloadedItems.value.push(id)
                }, delay)
                delay += 200
            })
        })
    }
})

provide("accordion-context", {
    activeTab,
    selectTab,
    registerTab,
    layout: props.layout,
    lazy: props.lazy,
    preloadedItems,
})
</script>

<template>
    <template v-if="props.layout == 'list'">
        <ul class="accordion-list">
            <slot />
        </ul>
    </template>
    <div class="accordion-enumeration" :class="{ 'accordion-enumeration--white': props.layout === 'backend' }" v-else>
        <div>
            <slot />
        </div>
    </div>
</template>
